/**
 * IndexedDB Storage Service for Subscription Licenses
 * Handles local caching of license tokens and entitlements
 * Supports both web browsers and Electron desktop apps
 */

const DB_NAME = 'bodapro_subscriptions';
const DB_VERSION = 1;
const STORES = {
    LICENSES: 'licenses',
    USAGE_QUEUE: 'usage_queue',
    SYNC_METADATA: 'sync_metadata'
};

// Detect if running in Electron/desktop
const isDesktop = () => {
    return (
        (window.bodapro && window.bodapro.platformType === 'desktop') ||
        (window.process && window.process.type === 'renderer') ||
        (window.navigator && window.navigator.userAgent && window.navigator.userAgent.includes('Electron'))
    );
};

// Fallback to localStorage if IndexedDB is not available
const useLocalStorageFallback = () => {
    try {
        return typeof(Storage) !== 'undefined' && window.localStorage !== null;
    } catch (e) {
        return false;
    }
};

class SubscriptionStorage {
    constructor() {
        this.db = null;
        this.initialized = false;
        this.useFallback = false;
        this.isDesktopApp = isDesktop();
    }

    async init() {
        if (this.initialized && (this.db || this.useFallback)) {
            return this.db || true;
        }

        // Try IndexedDB first (works in both web and Electron)
        if (window.indexedDB) {
            return new Promise((resolve, reject) => {
                const request = indexedDB.open(DB_NAME, DB_VERSION);

                request.onerror = () => {
                    console.warn('IndexedDB failed, falling back to localStorage:', request.error);
                    // Fall back to localStorage
                    this.useFallback = useLocalStorageFallback();
                    if (this.useFallback) {
                        this.initialized = true;
                        resolve(true);
                    } else {
                        reject(new Error('No storage available'));
                    }
                };

                request.onsuccess = () => {
                    this.db = request.result;
                    this.initialized = true;
                    this.useFallback = false;
                    resolve(this.db);
                };

                request.onupgradeneeded = (event) => {
                    const db = event.target.result;

                    // Licenses store
                    if (!db.objectStoreNames.contains(STORES.LICENSES)) {
                        const licenseStore = db.createObjectStore(STORES.LICENSES, { keyPath: 'extension_key' });
                        licenseStore.createIndex('expires_at', 'expires_at', { unique: false });
                        licenseStore.createIndex('last_synced', 'last_synced', { unique: false });
                    }

                    // Usage queue store
                    if (!db.objectStoreNames.contains(STORES.USAGE_QUEUE)) {
                        const usageStore = db.createObjectStore(STORES.USAGE_QUEUE, { keyPath: 'id', autoIncrement: true });
                        usageStore.createIndex('extension_key', 'extension_key', { unique: false });
                        usageStore.createIndex('timestamp', 'timestamp', { unique: false });
                        usageStore.createIndex('synced', 'synced', { unique: false });
                    }

                    // Sync metadata store
                    if (!db.objectStoreNames.contains(STORES.SYNC_METADATA)) {
                        db.createObjectStore(STORES.SYNC_METADATA, { keyPath: 'extension_key' });
                    }
                };
            });
        } else {
            // No IndexedDB, try localStorage fallback
            this.useFallback = useLocalStorageFallback();
            if (this.useFallback) {
                this.initialized = true;
                return Promise.resolve(true);
            } else {
                return Promise.reject(new Error('No storage available (IndexedDB or localStorage)'));
            }
        }
    }

    /**
     * Get cached license using localStorage fallback
     */
    async _getCachedLicenseFallback(extensionKey) {
        try {
            const data = localStorage.getItem(`subscription_license_${extensionKey}`);
            return data ? JSON.parse(data) : null;
        } catch (e) {
            console.error('Failed to read from localStorage:', e);
            return null;
        }
    }

    /**
     * Set cached license using localStorage fallback
     */
    async _setCachedLicenseFallback(extensionKey, licenseData) {
        try {
            localStorage.setItem(`subscription_license_${extensionKey}`, JSON.stringify({
                extension_key: extensionKey,
                ...licenseData,
                cached_at: new Date().toISOString(),
                last_synced: licenseData.last_synced || new Date().toISOString()
            }));
        } catch (e) {
            console.error('Failed to write to localStorage:', e);
            throw e;
        }
    }

    /**
     * Get cached license for an extension
     */
    async getCachedLicense(extensionKey) {
        await this.init();
        
        if (this.useFallback) {
            return this._getCachedLicenseFallback(extensionKey);
        }
        
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORES.LICENSES], 'readonly');
            const store = transaction.objectStore(STORES.LICENSES);
            const request = store.get(extensionKey);

            request.onsuccess = () => {
                resolve(request.result || null);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Cache license token and entitlements
     */
    async setCachedLicense(extensionKey, licenseData) {
        await this.init();
        
        if (this.useFallback) {
            await this._setCachedLicenseFallback(extensionKey, licenseData);
            return;
        }
        
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORES.LICENSES], 'readwrite');
            const store = transaction.objectStore(STORES.LICENSES);
            
            const data = {
                extension_key: extensionKey,
                ...licenseData,
                cached_at: new Date().toISOString(),
                last_synced: licenseData.last_synced || new Date().toISOString()
            };

            const request = store.put(data);

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Remove cached license
     */
    async removeCachedLicense(extensionKey) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORES.LICENSES], 'readwrite');
            const store = transaction.objectStore(STORES.LICENSES);
            const request = store.delete(extensionKey);

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Queue usage record for sync
     */
    async queueUsageRecord(usageData) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORES.USAGE_QUEUE], 'readwrite');
            const store = transaction.objectStore(STORES.USAGE_QUEUE);
            
            const data = {
                ...usageData,
                timestamp: usageData.timestamp || new Date().toISOString(),
                synced: false,
                queued_at: new Date().toISOString()
            };

            const request = store.add(data);

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Get queued usage records
     */
    async getQueuedUsageRecords(extensionKey = null, limit = 100) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORES.USAGE_QUEUE], 'readonly');
            const store = transaction.objectStore(STORES.USAGE_QUEUE);
            const index = store.index('synced');
            const request = index.getAll(false, limit);

            request.onsuccess = () => {
                let records = request.result;
                
                // Filter by extension if provided
                if (extensionKey) {
                    records = records.filter(r => r.extension_key === extensionKey);
                }

                resolve(records);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Mark usage record as synced
     */
    async markUsageRecordSynced(recordId) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORES.USAGE_QUEUE], 'readwrite');
            const store = transaction.objectStore(STORES.USAGE_QUEUE);
            const getRequest = store.get(recordId);

            getRequest.onsuccess = () => {
                const record = getRequest.result;
                if (record) {
                    record.synced = true;
                    record.synced_at = new Date().toISOString();
                    const putRequest = store.put(record);
                    
                    putRequest.onsuccess = () => resolve();
                    putRequest.onerror = () => reject(putRequest.error);
                } else {
                    resolve();
                }
            };

            getRequest.onerror = () => reject(getRequest.error);
        });
    }

    /**
     * Clear synced usage records (cleanup)
     */
    async clearSyncedUsageRecords(olderThanDays = 7) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORES.USAGE_QUEUE], 'readwrite');
            const store = transaction.objectStore(STORES.USAGE_QUEUE);
            const index = store.index('synced');
            const request = index.openCursor();

            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);
            let deletedCount = 0;

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    const record = cursor.value;
                    if (record.synced && record.synced_at) {
                        const syncedDate = new Date(record.synced_at);
                        if (syncedDate < cutoffDate) {
                            cursor.delete();
                            deletedCount++;
                        }
                    }
                    cursor.continue();
                } else {
                    resolve(deletedCount);
                }
            };

            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get sync metadata
     */
    async getSyncMetadata(extensionKey) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORES.SYNC_METADATA], 'readonly');
            const store = transaction.objectStore(STORES.SYNC_METADATA);
            const request = store.get(extensionKey);

            request.onsuccess = () => {
                resolve(request.result || null);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Set sync metadata
     */
    async setSyncMetadata(extensionKey, metadata) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORES.SYNC_METADATA], 'readwrite');
            const store = transaction.objectStore(STORES.SYNC_METADATA);
            
            const data = {
                extension_key: extensionKey,
                ...metadata,
                updated_at: new Date().toISOString()
            };

            const request = store.put(data);

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Clear all cached data (for testing or logout)
     */
    async clearAll() {
        await this.init();
        return Promise.all([
            new Promise((resolve, reject) => {
                const transaction = this.db.transaction([STORES.LICENSES], 'readwrite');
                const store = transaction.objectStore(STORES.LICENSES);
                const request = store.clear();
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            }),
            new Promise((resolve, reject) => {
                const transaction = this.db.transaction([STORES.USAGE_QUEUE], 'readwrite');
                const store = transaction.objectStore(STORES.USAGE_QUEUE);
                const request = store.clear();
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            }),
            new Promise((resolve, reject) => {
                const transaction = this.db.transaction([STORES.SYNC_METADATA], 'readwrite');
                const store = transaction.objectStore(STORES.SYNC_METADATA);
                const request = store.clear();
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            })
        ]);
    }
}

// Export singleton instance
const subscriptionStorage = new SubscriptionStorage();
export default subscriptionStorage;

